/**
 * Outbox of optimistic sends, keyed by clientId. Entries survive restarts (persisted with the paused
 * mutations that deliver them); the server dedupes retries on clientId.
 */
import type { Block, FileRef, GifRef, Message, StickerRef } from "@bundle/contracts";

export type PendingStatus = "sending" | "failed";
/**
 * `uploadIds`: local uploads (logic/files) the message waits for. It is posted only once they are all done
 * (`Files.attachmentsStatus`), so it may stay "sending" for as long as they take, offline included.
 */
export type PendingEntry = { message: Message; status: PendingStatus; uploadIds?: string[] };

export type SendInput = {
  clientId: string;
  channelId: string;
  threadRootId?: string;
  alsoSendToChannel?: boolean;
  /** May be empty when the message carries files (it is their caption). */
  text: string;
  createdAt: number;
  /** Local upload ids of the attachments, in order. */
  uploadIds?: string[];
  /** Optimistic file refs shown while the message is pending (local preview URLs). */
  files?: FileRef[];
  /** A sticker message (text "", no files): posted as `stickerId`, shown from this snapshot while pending. */
  sticker?: StickerRef;
  /** A GIF message (text "", no files). */
  gif?: GifRef;
};

/** Plain-text blocks for an optimistic message (the server parses the real AST). */
export function plainBlocks(text: string): Block[] {
  return text.split(/\n{2,}/).map((para) => ({ type: "paragraph", children: [{ type: "text", text: para }] }));
}

/** The optimistic message shown until the server's copy arrives (id = clientId, seq 0). */
export function pendingMessage(input: SendInput, authorId: string): Message {
  const m: Message = {
    id: input.clientId,
    clientId: input.clientId,
    channelId: input.channelId,
    seq: 0,
    authorId,
    text: input.text,
    blocks: input.text === "" ? [] : plainBlocks(input.text),
    createdAt: input.createdAt,
    deleted: false,
    reactions: [],
    replyCount: 0,
    replyUserIds: [],
    files: input.files ?? [],
  };
  if (input.threadRootId !== undefined) m.threadRootId = input.threadRootId;
  if (input.alsoSendToChannel === true) m.alsoSentToChannel = true;
  if (input.sticker !== undefined) m.sticker = input.sticker;
  if (input.gif !== undefined) m.gif = input.gif;
  return m;
}

/** Adds (or re-queues) an entry; an existing entry with the same clientId is replaced in place. */
export function insertPending(outbox: PendingEntry[], input: SendInput, authorId: string): PendingEntry[] {
  const entry: PendingEntry = { message: pendingMessage(input, authorId), status: "sending" };
  if (input.uploadIds !== undefined && input.uploadIds.length > 0) entry.uploadIds = input.uploadIds;
  const i = outbox.findIndex((e) => e.message.clientId === input.clientId);
  if (i < 0) return [...outbox, entry];
  const next = outbox.slice();
  next[i] = entry;
  return next;
}

/** The server accepted (or echoed) a message: drop the entry with its clientId. */
export function reconcileSent(outbox: PendingEntry[], m: Message): PendingEntry[] {
  return m.clientId === undefined ? outbox : removePending(outbox, m.clientId);
}

export function setStatus(outbox: PendingEntry[], clientId: string, status: PendingStatus): PendingEntry[] {
  let changed = false;
  const next = outbox.map((e) => {
    if (e.message.clientId !== clientId || e.status === status) return e;
    changed = true;
    return { ...e, status };
  });
  return changed ? next : outbox;
}

export function failPending(outbox: PendingEntry[], clientId: string): PendingEntry[] {
  return setStatus(outbox, clientId, "failed");
}

export function retryPending(outbox: PendingEntry[], clientId: string): PendingEntry[] {
  return setStatus(outbox, clientId, "sending");
}

export function removePending(outbox: PendingEntry[], clientId: string): PendingEntry[] {
  return outbox.some((e) => e.message.clientId === clientId) ? outbox.filter((e) => e.message.clientId !== clientId) : outbox;
}

/**
 * After a restart: "sending" entries whose delivery wasn't persisted (`liveClientIds` = clientIds of
 * restored/active send mutations) can't complete on their own → mark them failed so the user can retry.
 */
export function failOrphans(outbox: PendingEntry[], liveClientIds: string[]): PendingEntry[] {
  let next = outbox;
  for (const e of outbox) {
    const id = e.message.clientId;
    if (id !== undefined && e.status === "sending" && !liveClientIds.includes(id)) next = failPending(next, id);
  }
  return next;
}

/** Entries for a channel timeline (top-level + also-sent replies) or for one thread. */
export function pendingFor(outbox: PendingEntry[], channelId: string, threadRootId: string | undefined): PendingEntry[] {
  return outbox.filter(
    (p) =>
      p.message.channelId === channelId &&
      (threadRootId === undefined
        ? p.message.threadRootId === undefined || p.message.alsoSentToChannel === true
        : p.message.threadRootId === threadRootId),
  );
}

export function queuedCount(outbox: PendingEntry[]): number {
  let n = 0;
  for (const e of outbox) if (e.status === "sending") n += 1;
  return n;
}
